/**
 * @fileoverview FavoriteStar — toggleable star. Empty: `bp6-icon-star-empty`;
 * filled: `bp6-icon-star`. The canonical favorite/bookmark toggle anywhere in
 * the app — Settings rows, Sidebar entries, the app launcher.
 *
 * A REAL BUTTON. This was a `span` with `role="checkbox"`, `tabIndex={0}` and a
 * hand-written Enter/Space handler — a button reimplemented in four attributes,
 * missing the parts that are easy to forget (it was not in the tab order of
 * assistive tech that keys off the tag, and it had no `:focus-visible` ring
 * because the reset only targets real controls). Foundry uses a `button` here
 * too. The keyboard handler is gone because the element now does it natively.
 *
 * `aria-pressed`, not `aria-checked`: a checkbox is a form value that gets
 * submitted, a toggle button is an action with a sticky state. Screen readers
 * announce this one as "pressed"/"not pressed", which is what it is.
 */

import { Icon } from "@blueprintjs/core";

interface FavoriteStarProps {
  favorited: boolean;
  onToggle?: () => void;
  /** Localized label for screen readers (e.g. "Add to favorites"). */
  ariaLabel?: string;
  className?: string;
}

export function FavoriteStar({
  favorited,
  onToggle,
  ariaLabel = favorited ? "Remove from favorites" : "Add to favorites",
  className,
}: FavoriteStarProps) {
  return (
    <button data-testid="favorite-star"
      type="button"
      aria-pressed={favorited}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={[
        "bp-favorite-star",
        favorited ? "is-favorited" : "",
        className,
      ].filter(Boolean).join(" ")}
      onClick={(e) => {
        /*
          The star sits inside the app-card anchor, so a click that reaches the
          anchor would select the app as a side effect of starring it.
        */
        e.preventDefault();
        e.stopPropagation();
        onToggle?.();
      }}
    >
      <Icon icon={favorited ? "star" : "star-empty"} size={16} />
    </button>
  );
}
